import React, { useState, useEffect } from "react";
import {
  collection, addDoc, getDocs, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { fmtD, fmtDT } from "../crm/dateFormat";

// ─── Vijay-only access ────────────────────────────────────────────────────────
const VIJAY_NUMBERS = ["9494284529", "+919494284529"];
const isVijay = (user) => {
  if (!user) return false;
  const val = user.phone || user.mobile || user.id || user.uid || user.mobile2 || "";
  return VIJAY_NUMBERS.includes(String(val).trim());
};

// ─── Tab config ───────────────────────────────────────────────────────────────
const TABS = [
  { key: "specs",      label: "📋 Specs",       collection: "abcSpecs",      protected: false },
  { key: "logic",      label: "⚙️ Logic",        collection: "abcLogic",      protected: true  },
  { key: "roles",      label: "👥 Roles",        collection: "abcRoles",      protected: true  },
  { key: "manual",     label: "📖 User Manual",  collection: "abcUserManual", protected: true  },
  { key: "decisions",  label: "✅ Decisions",    collection: "abcDecisions",  protected: true  },
];

// ─── Specs tab config ─────────────────────────────────────────────────────────
const ENTRY_TYPES = [
  { value: "baseline",  label: "✅ Baseline",  color: "#4caf50" },
  { value: "mandatory", label: "🔴 Mandatory", color: "#f44336" },
  { value: "wishlist",  label: "🟡 Wishlist",  color: "#ff9800" },
  { value: "note",      label: "📝 Note",      color: "#6c63ff" },
];
const URGENCY = [
  { value: "high",   label: "🔴 High"   },
  { value: "medium", label: "🟡 Medium" },
  { value: "low",    label: "🟢 Low"    },
];
const TYPE_COLOR = Object.fromEntries(ENTRY_TYPES.map((t) => [t.value, t.color]));

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return fmtD(d);
};

const nowStamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
};

const nowReadable = () => {
  const d = new Date();
  return fmtDT(d);
};

// ─── Word export ──────────────────────────────────────────────────────────────
const buildTabHtml = (label, entries) => {
  if (!entries || entries.length === 0) return `<h2>${label}</h2><p style="color:#888">No entries.</p><hr/>`;
  const grouped = {};
  entries.forEach((e) => {
    const grp = e.screenName || e.category || e.role || e.section || "General";
    if (!grouped[grp]) grouped[grp] = [];
    grouped[grp].push(e);
  });
  let html = `<h2>${label}</h2>`;
  Object.entries(grouped).forEach(([grp, items]) => {
    html += `<h3>📍 ${grp}</h3>`;
    items.forEach((e) => {
      const typeLabel = ENTRY_TYPES.find((t) => t.value === e.type)?.label || e.type || "";
      html += `
        <h4>${e.title || e.name || "(No title)"}</h4>
        ${typeLabel ? `<p><strong>${typeLabel}</strong>${e.urgency ? ` &nbsp;|&nbsp; ${e.urgency}` : ""}</p>` : ""}
        <p>${(e.details || e.content || e.description || "").replace(/\n/g,"<br/>")}</p>
        <p style="color:#888;font-size:9pt;">
          Added: ${fmtDate(e.createdAt)}
          ${e.validatedAt ? " | Validated: " + fmtDate(e.validatedAt) : ""}
          ${e.lastUpdatedAt ? " | Updated: " + fmtDate(e.lastUpdatedAt) : ""}
          ${e.changeNote ? " | Change: " + e.changeNote : ""}
        </p><hr/>`;
    });
  });
  return html;
};

const wrapAndDownload = (bodyHtml, filename) => {
  const html = `
    <html xmlns:o='urn:schemas-microsoft-com:office:office'
          xmlns:w='urn:schemas-microsoft-com:office:word'
          xmlns='http://www.w3.org/TR/REC-html40'>
    <head><meta charset='utf-8'><title>ABC Spec</title>
    <style>
      body { font-family: Calibri, sans-serif; font-size: 11pt; }
      h1   { font-size: 16pt; color: #6c63ff; }
      h2   { font-size: 14pt; color: #333; border-bottom: 2px solid #6c63ff; padding-bottom: 4px; margin-top: 24px; }
      h3   { font-size: 12pt; color: #444; }
      h4   { font-size: 11pt; color: #555; margin-bottom: 2px; }
      p    { margin: 4px 0; }
      hr   { border: none; border-top: 1px solid #eee; margin: 10px 0; }
    </style></head><body>
    <h1>Adyantam — ABC Specification Document</h1>
    <p style="color:#888;font-size:9pt;">Downloaded: ${nowReadable()}</p>
    <hr/>
    ${bodyHtml}
    </body></html>`;
  const blob = new Blob(["\ufeff", html], { type: "application/msword" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ─── Blank forms ──────────────────────────────────────────────────────────────
const blankSpec = () => ({ screenName:"", type:"baseline", urgency:"", title:"", details:"", changeNote:"" });
const blankGeneric = () => ({ category:"", title:"", content:"", changeNote:"" });

// ─── Seed entries (specs only) ────────────────────────────────────────────────
const SEED_ENTRIES = [
  { screenName:"Dashboard", type:"wishlist", urgency:"🟡 Medium", title:"Monthly summary — income and expenses month-wise", details:"Prototype showed Apr/May/Jun income and expenses side by side on Dashboard. Verify if current app shows this breakdown or not." },
  { screenName:"Dashboard", type:"wishlist", urgency:"🟡 Medium", title:"Tanker cost trend on Dashboard", details:"Prototype showed tanker count, rate, and total for each month with an up arrow when cost increased." },
  { screenName:"Flats",     type:"wishlist", urgency:"🟡 Medium", title:"Show water dues alongside maintenance dues per flat", details:"Prototype showed both maintenance due and water due side by side for each flat." },
  { screenName:"Passbook",  type:"wishlist", urgency:"🟡 Medium", title:"Pocket payment reimbursement — Mark reimbursed button", details:"Prototype had a Mark Reimbursed button on each pocket payment expense." },
  { screenName:"Water",     type:"wishlist", urgency:"🟡 Medium", title:"Rate history summary on Water screen", details:"Prototype showed Rate Apr, Rate May+, Cost per litre, and Deposit amount as summary cards at the top." },
  { screenName:"Water",     type:"wishlist", urgency:"🟡 Medium", title:"Meter entry — edits remaining counter", details:"Prototype showed Edits remaining: 2 during meter reading entry, limiting corrections per month." },
];

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
const AbcSpecScreen = ({ user }) => {
  const [activeTab,      setActiveTab]      = useState("specs");
  const [allData,        setAllData]        = useState({ specs:[], logic:[], roles:[], manual:[], decisions:[] });
  const [loading,        setLoading]        = useState(true);
  const [showForm,       setShowForm]       = useState(false);
  const [editId,         setEditId]         = useState(null);
  const [specForm,       setSpecForm]       = useState(blankSpec());
  const [genericForm,    setGenericForm]    = useState(blankGeneric());
  const [filterType,     setFilterType]     = useState("all");
  const [filterScreen,   setFilterScreen]   = useState("all");
  const [saving,         setSaving]         = useState(false);
  const [msg,            setMsg]            = useState("");
  // Download modal
  const [showDownload,   setShowDownload]   = useState(false);
  const [dlSelected,     setDlSelected]     = useState({ specs:true, logic:true, roles:true, manual:true, decisions:true });
  // Protection modal
  const [protectModal,   setProtectModal]   = useState(null); // { action, id, entry } or null
  const [pendingAction,  setPendingAction]  = useState(null);

  // ── Load all collections ───────────────────────────────────────────────────
  const loadAll = async () => {
    setLoading(true);
    try {
      const results = {};
      for (const tab of TABS) {
        const q = tab.key === "specs"
  ? query(collection(db, tab.collection), orderBy("createdAt", "desc"))
  : query(collection(db, tab.collection));
        const snap = await getDocs(q);
        results[tab.key] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }
      setAllData(results);
    } catch (e) {
      setMsg("Error loading data: " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    const seedAndLoad = async () => {
      try {
        const snap = await getDocs(query(collection(db, "abcSpecs")));
        const existing = snap.docs.map((d) => d.data().title || "");
        for (const entry of SEED_ENTRIES) {
          if (!existing.includes(entry.title)) {
            await addDoc(collection(db, "abcSpecs"), { ...entry, createdAt: serverTimestamp() });
          }
        }
      } catch (e) {}
      await loadAll();
    };
    seedAndLoad();
  }, []);

  if (!isVijay(user)) return null;

  const currentTab = TABS.find((t) => t.key === activeTab);
  const isProtected = currentTab?.protected;
  const colName = currentTab?.collection;

  // ── Protection gate ────────────────────────────────────────────────────────
  const withProtection = (action) => {
    if (!isProtected) { action(); return; }
    setPendingAction(() => action);
    setProtectModal(true);
  };

  const confirmProtection = () => {
    setProtectModal(false);
    if (pendingAction) { pendingAction(); setPendingAction(null); }
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const isSpec = activeTab === "specs";
    const form   = isSpec ? specForm : genericForm;
    if (isSpec) {
      if (!form.screenName.trim() || !form.title.trim() || !form.details.trim()) {
        setMsg("Please fill Screen Name, Title, and Details."); return;
      }
    } else {
      if (!form.title.trim() || !form.content.trim()) {
        setMsg("Please fill Title and Content."); return;
      }
    }
    setSaving(true);
    try {
      if (editId) {
        await updateDoc(doc(db, colName, editId), { ...form, lastUpdatedAt: serverTimestamp() });
        setMsg("Entry updated.");
      } else {
        await addDoc(collection(db, colName), { ...form, createdAt: serverTimestamp() });
        setMsg("Entry added.");
      }
      setSpecForm(blankSpec()); setGenericForm(blankGeneric());
      setEditId(null); setShowForm(false);
      await loadAll();
    } catch (e) { setMsg("Save error: " + e.message); }
    setSaving(false);
  };

  // ── Validate (specs only) ─────────────────────────────────────────────────
  const handleValidate = async (id) => {
    try {
      await updateDoc(doc(db, colName, id), { validatedAt: serverTimestamp() });
      setMsg("Marked as validated."); await loadAll();
    } catch (e) { setMsg("Error: " + e.message); }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, colName, id));
      setMsg("Deleted."); await loadAll();
    } catch (e) { setMsg("Delete error: " + e.message); }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const handleEdit = (entry) => {
    if (activeTab === "specs") {
      setSpecForm({ screenName:entry.screenName||"", type:entry.type||"baseline", urgency:entry.urgency||"", title:entry.title||"", details:entry.details||"", changeNote:entry.changeNote||"" });
    } else {
      setGenericForm({ category:entry.category||entry.screenName||entry.role||entry.section||"", title:entry.title||entry.name||"", content:entry.content||entry.details||entry.description||"", changeNote:entry.changeNote||"" });
    }
    setEditId(entry.id); setShowForm(true);
    window.scrollTo({ top:0, behavior:"smooth" });
  };

  // ── Download ──────────────────────────────────────────────────────────────
  const handleDownloadSingle = () => {
    let body = "";
    TABS.forEach((t) => {
      if (!dlSelected[t.key]) return;
      body += buildTabHtml(t.label, allData[t.key]);
    });
    wrapAndDownload(body, `AbcSpec_ALL_${nowStamp()}.doc`);
    setShowDownload(false);
  };

  const handleDownloadSeparate = () => {
    TABS.forEach((t) => {
      if (!dlSelected[t.key]) return;
      const body = buildTabHtml(t.label, allData[t.key]);
      wrapAndDownload(body, `AbcSpec_${t.key}_${nowStamp()}.doc`);
    });
    setShowDownload(false);
  };

  // ── Filter helpers (specs tab) ────────────────────────────────────────────
  const entries     = allData[activeTab] || [];
  const allScreens  = [...new Set(entries.map((e) => e.screenName || e.category || e.role || e.section || "General"))].sort();
  const visible     = activeTab === "specs" ? entries.filter((e) => {
    const matchType   = filterType   === "all" || e.type === filterType;
    const matchScreen = filterScreen === "all" || (e.screenName||"General") === filterScreen;
    return matchType && matchScreen;
  }) : entries;

  const grouped = {};
  visible.forEach((e) => {
    const grp = e.screenName || e.category || e.role || e.section || "General";
    if (!grouped[grp]) grouped[grp] = [];
    grouped[grp].push(e);
  });

  // ── Styles ────────────────────────────────────────────────────────────────
  const S = {
    wrap:      { padding:"16px", maxWidth:"860px", margin:"0 auto", color:"#e0e0e0" },
    header:    { display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"10px", marginBottom:"12px" },
    h1:        { fontSize:"20px", fontWeight:"700", color:"#6c63ff", margin:0 },
    btnPurple: { background:"#6c63ff", color:"#fff", border:"none", borderRadius:"8px", padding:"8px 16px", cursor:"pointer", fontWeight:"600", fontSize:"14px" },
    btnGray:   { background:"#2a2a3a", color:"#ccc", border:"1px solid #444", borderRadius:"8px", padding:"6px 14px", cursor:"pointer", fontSize:"13px" },
    btnGreen:  { background:"#4caf50", color:"#fff", border:"none", borderRadius:"8px", padding:"6px 14px", cursor:"pointer", fontSize:"13px" },
    btnRed:    { background:"#c0392b", color:"#fff", border:"none", borderRadius:"8px", padding:"6px 14px", cursor:"pointer", fontSize:"13px" },
    btnOrange: { background:"#ff9800", color:"#fff", border:"none", borderRadius:"8px", padding:"8px 16px", cursor:"pointer", fontWeight:"600", fontSize:"14px" },
    tabs:      { display:"flex", gap:"4px", marginBottom:"16px", flexWrap:"wrap" },
    tab:       (active) => ({ padding:"8px 14px", borderRadius:"8px", cursor:"pointer", fontSize:"13px", fontWeight: active?"600":"400", background: active?"#6c63ff":"#1a1a24", color: active?"#fff":"#888", border: active?"none":"1px solid #2a2a3a" }),
    form:      { background:"#1a1a24", borderRadius:"12px", padding:"20px", marginBottom:"20px", border:"1px solid #333" },
    label:     { display:"block", marginBottom:"5px", fontSize:"13px", color:"#aaa" },
    input:     { width:"100%", background:"#0f0f13", border:"1px solid #444", borderRadius:"8px", padding:"9px 12px", color:"#e0e0e0", fontSize:"14px", boxSizing:"border-box", marginBottom:"12px" },
    textarea:  { width:"100%", background:"#0f0f13", border:"1px solid #444", borderRadius:"8px", padding:"9px 12px", color:"#e0e0e0", fontSize:"14px", boxSizing:"border-box", marginBottom:"12px", minHeight:"100px", resize:"vertical" },
    select:    { width:"100%", background:"#0f0f13", border:"1px solid #444", borderRadius:"8px", padding:"9px 12px", color:"#e0e0e0", fontSize:"14px", boxSizing:"border-box", marginBottom:"12px" },
    filters:   { display:"flex", gap:"10px", flexWrap:"wrap", marginBottom:"16px" },
    card:      { background:"#1a1a24", borderRadius:"10px", padding:"14px 16px", marginBottom:"12px", border:"1px solid #2a2a3a" },
    cardTitle: { fontWeight:"700", fontSize:"15px", marginBottom:"4px", color:"#fff" },
    badge:     (type) => ({ display:"inline-block", borderRadius:"6px", padding:"2px 10px", fontSize:"12px", fontWeight:"600", color:"#fff", background:TYPE_COLOR[type]||"#555", marginRight:"8px" }),
    meta:      { fontSize:"12px", color:"#777", marginTop:"6px" },
    details:   { fontSize:"14px", color:"#ccc", marginTop:"8px", whiteSpace:"pre-wrap", lineHeight:"1.6" },
    actions:   { display:"flex", gap:"8px", marginTop:"10px", flexWrap:"wrap" },
    sectionHd: { fontSize:"16px", fontWeight:"700", color:"#6c63ff", margin:"20px 0 10px", borderBottom:"1px solid #2a2a3a", paddingBottom:"6px" },
    msg:       { background:"#2a2a3a", borderRadius:"8px", padding:"10px 14px", marginBottom:"14px", fontSize:"13px", color:"#aaa" },
    overlay:   { position:"fixed", top:0, left:0, right:0, bottom:0, background:"#000000aa", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:"16px" },
    modal:     { background:"#1a1a24", borderRadius:"14px", padding:"24px", maxWidth:"420px", width:"100%", border:"1px solid #333" },
    modalTitle:{ fontSize:"16px", fontWeight:"700", color:"#ffa726", marginBottom:"10px" },
    modalText: { fontSize:"13px", color:"#ccc", marginBottom:"20px", lineHeight:"1.6" },
    checkbox:  { display:"flex", alignItems:"center", gap:"10px", marginBottom:"10px", fontSize:"14px", color:"#ccc", cursor:"pointer" },
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.wrap}>

      {/* Header */}
      <div style={S.header}>
        <h2 style={S.h1}>📘 ABC Specification Screen</h2>
        <div style={{ display:"flex", gap:"10px", flexWrap:"wrap" }}>
          <button style={S.btnOrange} onClick={() => setShowDownload(true)}>⬇️ Download</button>
          <button style={S.btnPurple} onClick={() => withProtection(() => { setShowForm(!showForm); setEditId(null); setSpecForm(blankSpec()); setGenericForm(blankGeneric()); })}>
            {showForm ? "✖ Cancel" : "➕ Add Entry"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {TABS.map((t) => (
          <div key={t.key} style={S.tab(activeTab===t.key)} onClick={() => { setActiveTab(t.key); setShowForm(false); setEditId(null); setFilterType("all"); setFilterScreen("all"); }}>
            {t.label}
            {t.protected && <span style={{ fontSize:"10px", marginLeft:"4px", opacity:0.6 }}>🔒</span>}
          </div>
        ))}
      </div>

      {/* Message */}
      {msg && <div style={S.msg}>{msg} <span style={{ cursor:"pointer", float:"right" }} onClick={() => setMsg("")}>✖</span></div>}

      {/* Add / Edit Form */}
      {showForm && (
        <div style={S.form}>
          <h3 style={{ margin:"0 0 16px", color:"#6c63ff" }}>{editId ? "✏️ Edit Entry" : "➕ New Entry"}</h3>

          {activeTab === "specs" ? (
            <>
              <label style={S.label}>Screen / Feature Name *</label>
              <input style={S.input} placeholder="e.g. Water Screen, Dashboard" value={specForm.screenName} onChange={(e) => setSpecForm({...specForm, screenName:e.target.value})} />
              <label style={S.label}>Entry Type *</label>
              <select style={S.select} value={specForm.type} onChange={(e) => setSpecForm({...specForm, type:e.target.value})}>
                {ENTRY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {(specForm.type==="wishlist"||specForm.type==="mandatory") && (
                <>
                  <label style={S.label}>Urgency</label>
                  <select style={S.select} value={specForm.urgency} onChange={(e) => setSpecForm({...specForm, urgency:e.target.value})}>
                    <option value="">— Select —</option>
                    {URGENCY.map((u) => <option key={u.value} value={u.label}>{u.label}</option>)}
                  </select>
                </>
              )}
              <label style={S.label}>Title *</label>
              <input style={S.input} placeholder="Short title" value={specForm.title} onChange={(e) => setSpecForm({...specForm, title:e.target.value})} />
              <label style={S.label}>Details *</label>
              <textarea style={S.textarea} placeholder="Full description" value={specForm.details} onChange={(e) => setSpecForm({...specForm, details:e.target.value})} />
              {editId && (
                <>
                  <label style={S.label}>Change Note (why editing?)</label>
                  <input style={S.input} placeholder="e.g. Updated tanker limit" value={specForm.changeNote} onChange={(e) => setSpecForm({...specForm, changeNote:e.target.value})} />
                </>
              )}
            </>
          ) : (
            <>
              <label style={S.label}>Category / Screen / Role *</label>
              <input style={S.input} placeholder={activeTab==="roles"?"e.g. President, Watchman":"e.g. Water Screen, Dashboard"} value={genericForm.category} onChange={(e) => setGenericForm({...genericForm, category:e.target.value})} />
              <label style={S.label}>Title *</label>
              <input style={S.input} placeholder="Short title" value={genericForm.title} onChange={(e) => setGenericForm({...genericForm, title:e.target.value})} />
              <label style={S.label}>Content *</label>
              <textarea style={S.textarea} placeholder="Full content in plain English" value={genericForm.content} onChange={(e) => setGenericForm({...genericForm, content:e.target.value})} />
              {editId && (
                <>
                  <label style={S.label}>Change Note (why editing?)</label>
                  <input style={S.input} placeholder="Reason for edit" value={genericForm.changeNote} onChange={(e) => setGenericForm({...genericForm, changeNote:e.target.value})} />
                </>
              )}
            </>
          )}

          <div style={{ display:"flex", gap:"10px" }}>
            <button style={S.btnPurple} onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editId ? "💾 Update Entry" : "💾 Save Entry"}
            </button>
            <button style={S.btnGray} onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Filters (specs tab only) */}
      {activeTab === "specs" && (
        <div style={S.filters}>
          <select style={{...S.select, width:"auto", marginBottom:0}} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            {ENTRY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select style={{...S.select, width:"auto", marginBottom:0}} value={filterScreen} onChange={(e) => setFilterScreen(e.target.value)}>
            <option value="all">All Screens</option>
            {allScreens.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ alignSelf:"center", fontSize:"13px", color:"#777" }}>{visible.length} entr{visible.length===1?"y":"ies"}</span>
        </div>
      )}

      {/* Entries */}
      {loading ? (
        <p style={{ color:"#777" }}>Loading…</p>
      ) : visible.length === 0 ? (
        <p style={{ color:"#777" }}>No entries yet. Tap ➕ Add Entry to start.</p>
      ) : (
        Object.entries(grouped).map(([grp, items]) => (
          <div key={grp}>
            <div style={S.sectionHd}>📍 {grp}</div>
            {items.map((entry) => (
              <div key={entry.id} style={{ ...S.card, borderLeft:`4px solid ${TYPE_COLOR[entry.type]||"#6c63ff"}` }}>
                <div style={S.cardTitle}>{entry.title || entry.name || "(No title)"}</div>
                {entry.type && (
                  <div>
                    <span style={S.badge(entry.type)}>{ENTRY_TYPES.find((t)=>t.value===entry.type)?.label||entry.type}</span>
                    {entry.urgency && <span style={{ fontSize:"12px", color:"#aaa" }}>{entry.urgency}</span>}
                  </div>
                )}
                <div style={S.details}>{entry.details || entry.content || entry.description || ""}</div>
                <div style={S.meta}>
                  Added: {fmtDate(entry.createdAt)}
                  {entry.validatedAt && <span style={{ color:"#4caf50" }}> &nbsp;✅ Validated: {fmtDate(entry.validatedAt)}</span>}
                  {entry.lastUpdatedAt && <span> &nbsp;| Updated: {fmtDate(entry.lastUpdatedAt)}</span>}
                  {entry.changeNote && <span style={{ color:"#ff9800" }}> &nbsp;| Change: {entry.changeNote}</span>}
                </div>
                <div style={S.actions}>
                  {activeTab==="specs" && !entry.validatedAt && (
                    <button style={S.btnGreen} onClick={() => handleValidate(entry.id)}>✅ Mark Validated</button>
                  )}
                  <button style={S.btnGray} onClick={() => withProtection(() => handleEdit(entry))}>✏️ Edit</button>
                  <button style={S.btnRed}  onClick={() => withProtection(() => handleDelete(entry.id))}>🗑 Delete</button>
                </div>
              </div>
            ))}
          </div>
        ))
      )}

      {/* ── Protection Warning Modal ── */}
      {protectModal && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <div style={S.modalTitle}>⚠️ Protected Content</div>
            <div style={S.modalText}>
              You are about to edit or delete a reference entry in <strong>{currentTab?.label}</strong>.<br/><br/>
              This section contains important documentation. Incorrect changes may affect accuracy.<br/><br/>
              Are you sure you want to continue?
            </div>
            <div style={{ display:"flex", gap:"10px" }}>
              <button style={S.btnRed} onClick={confirmProtection}>Yes, I know what I'm doing</button>
              <button style={S.btnGray} onClick={() => { setProtectModal(false); setPendingAction(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Download Modal ── */}
      {showDownload && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <div style={{ fontSize:"16px", fontWeight:"700", color:"#6c63ff", marginBottom:"16px" }}>⬇️ Download Options</div>
            <div style={{ fontSize:"13px", color:"#aaa", marginBottom:"14px" }}>Select sections to download:</div>

            {TABS.map((t) => (
              <label key={t.key} style={S.checkbox}>
                <input type="checkbox" checked={dlSelected[t.key]} onChange={(e) => setDlSelected({...dlSelected, [t.key]:e.target.checked})} />
                {t.label}
              </label>
            ))}

            <div style={{ fontSize:"11px", color:"#555", marginBottom:"16px", marginTop:"4px" }}>
              Timestamp: {nowReadable()}
            </div>

            <div style={{ display:"flex", gap:"10px", flexWrap:"wrap" }}>
              <button style={S.btnPurple} onClick={handleDownloadSingle}>📄 One Document</button>
              <button style={S.btnGreen}  onClick={handleDownloadSeparate}>📂 Separate Files</button>
              <button style={S.btnGray}   onClick={() => setShowDownload(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AbcSpecScreen;